import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Phone, Mail, Facebook, Twitter, Linkedin, Youtube, Send } from 'lucide-react';
import { contactInfo, socialMedia, quickLinks } from '../mock';
import { useToast } from '../hooks/use-toast';

const footerSections = {
  phone: {
    visible: false
  }
};

const Footer = () => {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const { toast } = useToast();

  const handleSubscribe = (e) => {
    e.preventDefault();
    if (email && name) {
      toast({
        title: '¡Suscripción exitosa!',
        description: 'Te has suscrito al boletín de CENID.',
      });
      setEmail('');
      setName('');
    }
  };

  const iconMap = {
    Facebook: Facebook,
    Twitter: Twitter,
    Linkedin: Linkedin,
    Youtube: Youtube
  };

  return (
    <footer className="bg-gradient-to-b from-gray-900 to-gray-950 text-white">
      {/* Newsletter Section */}
      <div className="bg-[#0b742f] py-12">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="text-center md:text-left">
              <h3 className="text-2xl font-bold mb-2">Suscríbete al Boletín</h3>
              <p className="text-green-100">Mantente informado sobre nuestras actividades y convocatorias</p>
            </div>
            <form onSubmit={handleSubscribe} className="flex flex-col sm:flex-row gap-3 w-full md:w-auto">
              <input
                type="text"
                placeholder="Nombre"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="px-4 py-3 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#e87729] w-full sm:w-48"
                required
              />
              <input
                type="email"
                placeholder="Correo electrónico"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="px-4 py-3 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#e87729] w-full sm:w-64"
                required
              />
              <button
                type="submit"
                className="bg-[#e87729] hover:bg-[#d66820] text-white font-semibold px-6 py-3 rounded-lg transition-all duration-300 flex items-center justify-center gap-2 hover:scale-105"
              >
                <Send className="w-4 h-4" />
                Suscribirse
              </button>
            </form>
          </div>
        </div>
      </div>

      {/* Main Footer Content */}
      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Logo and About */}
          <div className="space-y-4">
            <img
              src="https://recursos.cenid.org/img_cenid/cenid.png"
              alt="CENID Logo"
              className="h-16 w-auto brightness-0 invert"
            />
            <p className="text-gray-400 text-sm leading-relaxed">
              Centro de Estudios e Investigaciones para el Desarrollo Docente dedicado a la excelencia académica y la investigación.
            </p>
            {/* Social Media */}
            <div className="flex gap-3 pt-2">
              {socialMedia.map((social, index) => {
                const Icon = iconMap[social.icon];
                return (
                  <a
                    key={index}
                    href={social.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-gray-800 hover:bg-[#0b742f] p-2.5 rounded-full transition-all duration-300 hover:scale-110"
                  >
                    <Icon className="w-5 h-5" />
                  </a>
                );
              })}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-lg font-bold mb-4 text-[#e87729]">Enlaces Rápidos</h4>
            <ul className="space-y-2">
              {quickLinks.map((link, index) => (
                <li key={index}>
                  <Link
                    to={link.url}
                    className="text-gray-400 hover:text-[#e87729] transition-colors duration-200 text-sm flex items-center gap-2 group"
                  >
                    <span className="w-0 h-0.5 bg-[#e87729] group-hover:w-4 transition-all duration-300"></span>
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-lg font-bold mb-4 text-[#e87729]">Contacto</h4>
            <ul className="space-y-3 text-sm">
              <li className="flex items-start gap-3 text-gray-400">
                <MapPin className="w-5 h-5 text-[#e87729] flex-shrink-0 mt-0.5" />
                <span>{contactInfo.address}<br />{contactInfo.city}</span>
              </li>
              {footerSections.phone.visible && (
                <li className="flex items-center gap-3 text-gray-400">
                  <Phone className="w-5 h-5 text-[#e87729] flex-shrink-0" />
                  <a href={`tel:${contactInfo.phone}`} className="hover:text-[#e87729] transition-colors duration-200">
                    {contactInfo.phone}
                  </a>
                </li>
              )}
              <li className="flex items-center gap-3 text-gray-400">
                <Mail className="w-5 h-5 text-[#e87729] flex-shrink-0" />
                <a href={`mailto:${contactInfo.email}`} className="hover:text-[#e87729] transition-colors duration-200">
                  {contactInfo.email}
                </a>
              </li>
            </ul>
          </div>

          {/* Programs */}
          <div>
            <h4 className="text-lg font-bold mb-4 text-[#e87729]">Programas</h4>
            <ul className="space-y-2 text-sm">
              <li>
                <Link to="/formacion/maestria" className="text-gray-400 hover:text-[#e87729] transition-colors duration-200 block">
                  Maestría en Educación
                </Link>
              </li>
              <li>
                <Link to="/formacion/doctorado" className="text-gray-400 hover:text-[#e87729] transition-colors duration-200 block">
                  Doctorado en Educación
                </Link>
              </li>
              <li>
                <Link to="/formacion/postdoctorado" className="text-gray-400 hover:text-[#e87729] transition-colors duration-200 block">
                  Postdoctorado en Educación
                </Link>
              </li>
              <li>
                <Link to="/congresos" className="text-gray-400 hover:text-[#e87729] transition-colors duration-200 block">
                  Congresos Internacionales
                </Link>
              </li>
              <li>
                <Link to="/editorial/revistas" className="text-gray-400 hover:text-[#e87729] transition-colors duration-200 block">
                  Revistas Electrónicas
                </Link>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-gray-800">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4 text-sm text-gray-400">
            <div className="flex gap-4">
              <Link to="/nosotros/aviso-privacidad" className="hover:text-[#e87729] transition-colors duration-200">
                Aviso de Privacidad
              </Link>
              <span className="text-gray-600">•</span>
              <Link to="/politica-cookies" className="hover:text-[#e87729] transition-colors duration-200">
                Política de Cookies
              </Link>
              <span className="text-gray-600">•</span>
              <button 
                onClick={() => window.dispatchEvent(new CustomEvent('openCookieSettings'))}
                className="hover:text-[#e87729] transition-colors duration-200 cursor-pointer"
              >
                Configurar Cookies
              </button>
            </div>
            <div>
              <p>&copy; {new Date().getFullYear()} CENID. Todos los derechos reservados.</p>
            </div>
            <div>
              <a
                href="https://programacioninteligente.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-[#e87729] transition-colors duration-200"
              >
                Diseño por ProgramaciónInteligente.com
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
